import React, { useEffect } from 'react';
import T from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Grid, Button } from '@mui/material';
import { StyledContent, StyledHandlers, StyledNewDialog } from '../elements';
import { updateMachineAutomationSystemConfig } from 'redux/machines/actions';
import { IrisV2RockwellConfigurationSchema } from 'attrs/formValidation';
import { Formik } from 'formik';
import { FormElements } from 'web-components';
import { useSelector, useDispatch } from 'react-redux';
import { getAutomationSystemConfig } from 'redux/machines/selectors';
import { loadMachineAutomationSystemConfig } from '../../../../redux/machines/actions';
import { SUCCESS } from 'attrs/status';

const EditAutomationSystemConfigurationIRISV2Rockwell = ({ open, handleClose, machineId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data: automationSystemConfig, loadingState: automationSystemConfigLoadingState } =
    useSelector(getAutomationSystemConfig);

  useEffect(() => {
    dispatch(loadMachineAutomationSystemConfig(machineId));
  }, [dispatch, machineId]);

  const handleUpdateAutomationSystemConfiguration = values => {
    const iris_v2_rockwell_payload = {
      iris_v2_rockwell: {
        address: values.address,
        port: 0,
        rack: 0,
        slot: Number(values.slot)
      },
    };
    dispatch(updateMachineAutomationSystemConfig(machineId, iris_v2_rockwell_payload));
    handleClose();
  };

  if (automationSystemConfigLoadingState.status !== SUCCESS) return null;

  return (
    <Formik
      initialValues={{
        address: automationSystemConfig.iris_v2_rockwell.address,
        slot: automationSystemConfig.iris_v2_rockwell.slot
      }}
      validationSchema={IrisV2RockwellConfigurationSchema}
      onSubmit={handleUpdateAutomationSystemConfiguration}
    >
      {({ values, handleChange, handleBlur, handleSubmit, errors }) => {
        const actionBar = (
          <StyledHandlers>
            <Button color="secondary" onClick={handleClose}>
              {t('form.cancel')}
            </Button>
            <Button
              variant="contained"
              style={{ padding: '1rem 2.5rem' }}
              onClick={handleSubmit}
              disabled={errors.address || errors.slot}
            >
              {t('form.save')}
            </Button>
          </StyledHandlers>
        );

        return (
          <StyledNewDialog
            open={open}
            handleClose={handleClose}
            title={t('machines.detail.automation_system.configuration.edit_automation_system_configuration')}
            subTitle={t('machines.detail.automation_system.configuration.subtitle')}
            maxWidth="1062px"
            displayActionBar="true"
            actionBar={actionBar}
            content={
              <StyledContent>
                <form>
                  <Grid container spacing={2} columns={12}>
                    <Grid item xs={6}>
                      <FormElements.TextField
                        fullWidth
                        label={t('machines.detail.automation_system.configuration.ip_address')}
                        id="address"
                        margin="normal"
                        name="address"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.address}
                        required
                        type="text"
                        error={errors.address}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormElements.TextField
                        fullWidth
                        label={t('machines.detail.automation_system.configuration.slot')}
                        id="slot"
                        margin="normal"
                        name="slot"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.slot}
                        required
                        type="number"
                        error={errors.slot}
                      />
                    </Grid>
                  </Grid>
                </form>
              </StyledContent>
            }
          />
        );
      }}
    </Formik>
  );
};

EditAutomationSystemConfigurationIRISV2Rockwell.propTypes = {
  open: T.bool.isRequired,
  handleClose: T.func.isRequired,
  machineId: T.string.isRequired,
  automationSystem: T.shape({
    plc_type: T.string
  }).isRequired
};

export default EditAutomationSystemConfigurationIRISV2Rockwell;
